import { cloneDeep } from 'es-toolkit';
import { Workflows } from '../../services';
import FormLayouts from '../../services/rest/FormLayouts';
import EmailLayouts from '../../services/rest/EmailLayouts';
import Articles from '../../services/rest/Articles';
import Salepoints from '../../services/rest/Salepoints';
import {
    collectBrickReferencedResourceIds,
    persistBrickLayoutIdsForSave,
    reconcileWorkflowGraphReferences,
    remapEditorLayoutAfterBrickIdChanges,
} from '../../containers/Workflows/workflowFlowUtils';
import { Workflow } from '../../models';
import { createRestSlices, applyReducers } from './rest';

const {
    initialState,
    getReducer, listReducer, updateReducer, duplicateReducer,
    startCreateReducer, createReducer, deleteReducer,
    getAction, listAction, duplicateAction: duplicateWorkflowRequest,
    startCreateAction, createAction, deleteAction,
    linkedResourcesReducer, linkedResourcesAction
} = createRestSlices(Workflows);

const WORKFLOW_UPDATE_UPDATING = `kronos/${Workflows.resource}/UPDATING_ITEM`;
const WORKFLOW_UPDATE_SUCCESS = `kronos/${Workflows.resource}/UPDATING_ITEM_SUCCESS`;
const WORKFLOW_UPDATE_FAILURE = `kronos/${Workflows.resource}/UPDATING_ITEM_FAILURE`;
/** Non-blocking API warnings after workflow save (e.g. missing brick input mapping). */
const WORKFLOW_BINDING_WARNINGS = 'kronos/workflows/BINDING_WARNINGS';
/** Apply client workflow state before PUT returns (graph + form stay in sync; rollback on FAILURE). */
const WORKFLOW_OPTIMISTIC_PATCH = 'kronos/workflows/OPTIMISTIC_PATCH';

const STARTING_WORKFLOW                  = 'kronos/workflows/STARTING_WORKFLOW';
const STARTING_WORKFLOW_SUCCESS          = 'kronos/workflows/STARTING_WORKFLOW_SUCCESS';
const STARTING_WORKFLOW_FAILURE          = 'kronos/workflows/STARTING_WORKFLOW_FAILURE';
const RESUMING_WORKFLOW                  = 'kronos/workflows/RESUMING_WORKFLOW';
const RESUMING_WORKFLOW_SUCCESS          = 'kronos/workflows/RESUMING_WORKFLOW_SUCCESS';
const RESUMING_WORKFLOW_FAILURE          = 'kronos/workflows/RESUMING_WORKFLOW_FAILURE';
const DONE_BRICK                         = 'kronos/workflows/DONE_BRICK';
const DONE_BRICK_SUCCESS                 = 'kronos/workflows/DONE_BRICK_SUCCESS';
const DONE_BRICK_FAILURE                 = 'kronos/workflows/DONE_BRICK_FAILURE';
const GET_BRICK_RESOURCES                = 'kronos/workflows/GET_BRICK_RESOURCES';
const GET_BRICK_RESOURCES_SUCCESS        = 'kronos/workflows/GET_BRICK_RESOURCES_SUCCESS';
const GET_BRICK_RESOURCES_FAILURE        = 'kronos/workflows/GET_BRICK_RESOURCES_FAILURE';

const WORKFLOWS_MERGE_LINKED_RESOURCES = 'kronos/workflows/MERGE_LINKED_RESOURCES';

/**
 * @param {object[]|undefined} existing
 * @param {object[]|undefined} additions
 * @param {string} id_field
 * @returns {object[]}
 */
function merge_linked_resource_arrays(existing, additions, id_field) {
    const map = new Map();
    const should_replace = (prev, next) => {
        if (!prev) {
            return true;
        }
        if (id_field !== 'id') {
            return false;
        }
        const prev_fields = Array.isArray(prev.fields) ? prev.fields.length : 0;
        const next_fields = Array.isArray(next.fields) ? next.fields.length : 0;
        return next_fields > prev_fields;
    };

    for (const item of existing || []) {
        if (item != null && item[id_field] != null)
            map.set(String(item[id_field]), item);
    }
    for (const item of additions || []) {
        if (item == null || item[id_field] == null) {
            continue;
        }
        const key = String(item[id_field]);
        const prev = map.get(key);
        if (!map.has(key) || should_replace(prev, item)) {
            map.set(key, item);
        }
    }
    return Array.from(map.values());
}

const reducer = (state = initialState, action) => {
    switch(action.type) {
        case STARTING_WORKFLOW:
            return {
                ...state,
                isLoading: true,
                hasLoadingError: false,
                errorMessage: null,
                workflow: null
            };
        case STARTING_WORKFLOW_SUCCESS:
            return {
                ...state,
                isLoading: false,
                errorMessage: null,
                workflow: action.workflow
            };
        case STARTING_WORKFLOW_FAILURE:
        case RESUMING_WORKFLOW_FAILURE:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: true,
                errorMessage: action.error.message,
                workflow: null
            };
        case RESUMING_WORKFLOW:
            return {
                ...state,
                isLoading: true,
                hasLoadingError: false,
                errorMessage: null,
                workflow: null
            };
        case RESUMING_WORKFLOW_SUCCESS:
            return {
                ...state,
                isLoading: false,
                errorMessage: null,
                workflow: action.workflow
            };
        case DONE_BRICK:
            return {
                ...state,
                isLoading: true,
                hasLoadingError: false,
                errorMessage: null,
            };
        case DONE_BRICK_SUCCESS:
            return {
                ...state,
                isLoading: false,
                errorMessage: null,
                workflow: action.workflow
            };
        case DONE_BRICK_FAILURE:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: true,
                errorMessage: action.error.message,
                workflow: null
            };
        case GET_BRICK_RESOURCES:
            return {
                ...state,
                isLoading: true,
                hasLoadingError: false,
                errorMessage: null,
            };
        case GET_BRICK_RESOURCES_SUCCESS:
            state.workflow.bricks = state.workflow.bricks.map(b => {
                if (b.id === action.brick_id)
                    b.resources = action.resources;
                return b;
            });
            return {
                ...state,
                isLoading: false,
                errorMessage: null,
            };
        case GET_BRICK_RESOURCES_FAILURE:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: true,
                errorMessage: action.error.message
            };
        case WORKFLOWS_MERGE_LINKED_RESOURCES: {
            const prev = state.linkedResources && typeof state.linkedResources === 'object'
                ? state.linkedResources
                : {};
            const p = action.patch || {};
            return {
                ...state,
                linkedResources: {
                    ...prev,
                    ...(p.forms != null
                        ? { forms: merge_linked_resource_arrays(prev.forms, p.forms, 'id') }
                        : {}),
                    ...(p.emails != null
                        ? { emails: merge_linked_resource_arrays(prev.emails, p.emails, '_id') }
                        : {}),
                    ...(p.articles != null
                        ? { articles: merge_linked_resource_arrays(prev.articles, p.articles, '_id') }
                        : {}),
                    ...(p.salepoints != null
                        ? { salepoints: merge_linked_resource_arrays(prev.salepoints, p.salepoints, '_id') }
                        : {}),
                },
            };
        }
        case WORKFLOW_OPTIMISTIC_PATCH:
            return {
                ...state,
                workflow: action.workflow,
            };
        case WORKFLOW_UPDATE_UPDATING:
            return {
                ...state,
                workflowBindingWarnings: [],
            };
        case WORKFLOW_BINDING_WARNINGS:
            return {
                ...state,
                workflowBindingWarnings: Array.isArray(action.warnings) ? action.warnings : [],
            };
        case WORKFLOW_UPDATE_FAILURE:
            if (action.rollbackWorkflow !== undefined)
                return {
                    ...state,
                    workflow: action.rollbackWorkflow,
                    workflowBindingWarnings: [],
                };
            return {
                ...state,
                workflowBindingWarnings: [],
            };
        default:
            return state;
    }
};

/* Export reducer */
export default (state = initialState, action) => {
    return applyReducers(state, action, [
        getReducer, listReducer, updateReducer, duplicateReducer,
        startCreateReducer, createReducer, deleteReducer,
        linkedResourcesReducer, reducer
    ]);
}

function startingWorkflow() { return { type: STARTING_WORKFLOW } }
function startingWorkflowSuccess(workflow) { return { type: STARTING_WORKFLOW_SUCCESS, workflow } }
function startingWorkflowFailure(err) { return { type: STARTING_WORKFLOW_FAILURE, error: err } }
function resumingWorkflow() { return { type: RESUMING_WORKFLOW } }
function resumingWorkflowSuccess(workflow) { return { type: RESUMING_WORKFLOW_SUCCESS, workflow } }
function resumingWorkflowFailure(err) { return { type: RESUMING_WORKFLOW_FAILURE, error: err } }
const startWorkflowAction = (workflow_id, tkt_instance_id, context) => {
    return (dispatch) => {
        dispatch(startingWorkflow());
        Workflows.startWorkflow(workflow_id, tkt_instance_id, context)
            .then(data => {
                const { workflow } = data;
                dispatch(startingWorkflowSuccess(workflow));
            })
            .catch(err => {
                dispatch(startingWorkflowFailure(err))
            });
    }
};

/**
 * Resume a persisted workflow run and load its runnable state in Redux.
 *
 * @param {Object} params
 * @param {?string} params.run_id
 * @param {?string} params.event
 * @param {?string} params.cart_id
 * @param {?string} params.resume_token
 * @param {?Object} params.resume_context
 * @param {?string} params.comment
 * @param {?Function} callback
 */
const resumeWorkflowRunAction = (params = {}, callback) => {
    return (dispatch) => {
        dispatch(resumingWorkflow());
        Workflows.resumeRun(
            params.run_id ?? null,
            params.event ?? null,
            params.cart_id ?? null,
            params.resume_token ?? null,
            params.resume_context ?? null,
            params.comment ?? null
        ).then((data) => {
            const { workflow } = data;
            dispatch(resumingWorkflowSuccess(workflow));
            callback && callback(/* err */ null, workflow, data.workflow_run || null);
        }).catch((err) => {
            dispatch(resumingWorkflowFailure(err));
            callback && callback(err);
        });
    };
};

function _doneBrick() { return { type: DONE_BRICK } }
function doneBrickSuccess(workflow) { return { type: DONE_BRICK_SUCCESS, workflow } }
function doneBrickFailure(err) { return { type: DONE_BRICK_FAILURE, error: err } }
const doneBrickAction = (workflow_id, tkt_instance_id, context, callback) => {
    return (dispatch) => {
        dispatch(_doneBrick());
        Workflows.doneBrick(workflow_id, tkt_instance_id, context)
            .then(data => {
                const { workflow } = data;
                dispatch(doneBrickSuccess(workflow));
                callback && callback(/*err*/null, workflow);
            })
            .catch(err => {
                dispatch(doneBrickFailure(err))
                callback && callback(err);
            });
    }
};

function gettingBrickResources() { return { type: GET_BRICK_RESOURCES } }
function gettingBrickResourcesSuccess(brick_id, resources) { return { type: GET_BRICK_RESOURCES_SUCCESS, brick_id, resources } }
function gettingBrickResourcesFailure(err) { return { type: GET_BRICK_RESOURCES_FAILURE, error: err } }
const getBrickResourcesAction = (workflow_id, tkt_instance_id, brick_id) => {
    return (dispatch) => {
        dispatch(gettingBrickResources());
        Workflows.getBrickResources(workflow_id, tkt_instance_id, brick_id)
            .then(data => {
                const { resources } = data;
                dispatch(gettingBrickResourcesSuccess(brick_id, resources));
            })
            .catch(err => {
                dispatch(gettingBrickResourcesFailure(err))
            });
    }
};

/* Export CRUD actions */
export const startCreateWorkflow         = startCreateAction;
export const createWorkflow              = createAction;
export const loadWorkflow                = getAction;
export const loadWorkflows               = listAction;

/**
 * Apply graph edits locally without persisting to the API.
 *
 * @param {object} workflow
 * @returns {{ type: string, workflow: object }}
 */
export const patchWorkflowLocally = (workflow) => ({
    type: WORKFLOW_OPTIMISTIC_PATCH,
    workflow: workflow instanceof Workflow ? workflow : new Workflow(workflow),
});

/**
 * Persists workflow then rewrites `editor_layout` node ids when new bricks lost `_clientLayoutId`
 * server-side so flow edges still match brick ids (see `remapEditorLayoutAfterBrickIdChanges`).
 */
export const updateWorkflow = (item, callback) => {
    return (dispatch, getState) => {
        const bricks = persistBrickLayoutIdsForSave(Array.isArray(item?.bricks) ? item.bricks : []);
        const reconciled_layout = reconcileWorkflowGraphReferences(bricks, item?.editor_layout);
        const to_save = {
            ...item,
            bricks,
            editor_layout: reconciled_layout,
        };
        const pre_save = cloneDeep(to_save);
        const has_id = Boolean(item?.id || item?._id);
        const current = getState().workflows?.workflow;
        const rollback = has_id && current ? cloneDeep(current) : undefined;
        if (has_id && current)
            dispatch({ type: WORKFLOW_OPTIMISTIC_PATCH, workflow: to_save });
        dispatch({ type: WORKFLOW_UPDATE_UPDATING });
        return Workflows.update(to_save)
            .then((data) => {
                let wf = data.workflow;
                wf = remapEditorLayoutAfterBrickIdChanges(wf, pre_save);
                dispatch({ type: WORKFLOW_UPDATE_SUCCESS, workflow: wf });
                const warns = Array.isArray(data.flash?.warnings) ? data.flash.warnings : [];
                dispatch({ type: WORKFLOW_BINDING_WARNINGS, warnings: warns });
                callback?.(/* err */ null, wf);
            })
            .catch((err) => {
                dispatch({
                    type: WORKFLOW_UPDATE_FAILURE,
                    error: err,
                    ...(rollback !== undefined ? { rollbackWorkflow: rollback } : {}),
                });
                callback?.(err);
            });
    };
};

export const deleteWorkflow              = deleteAction;
export const duplicateWorkflow = (item, callback) => {
    return (dispatch) => {
        const id = item?.id ?? item?._id;
        if (!id) {
            const err = new Error('Workflow id is required');
            callback?.(err);
            return Promise.reject(err);
        }

        return Workflows.get(id)
            .then((data) => duplicateWorkflowRequest(data.workflow, callback)(dispatch));
    };
};
export const startWorkflow               = startWorkflowAction;
export const resumeWorkflowRun           = resumeWorkflowRunAction;
export const doneBrick                   = doneBrickAction;
export const getBrickResources           = getBrickResourcesAction;
export const getWorkflowsLinkedResources = linkedResourcesAction;

function mergeWorkflowLinkedResources(patch) {
    return { type: WORKFLOWS_MERGE_LINKED_RESOURCES, patch };
}

/**
 * Fetches forms, email layouts, articles and salepoints referenced by bricks but missing from linkedResources,
 * then merges them into state (so graph labels and brick forms always resolve names).
 */
export const preloadWorkflowBrickReferencedResources = (workflow) => {
    return async (dispatch, getState) => {
        if (!workflow?.bricks?.length)
            return;

        const { formIds, emailLayoutIds, articleIds, salepointIds } = collectBrickReferencedResourceIds(workflow.bricks);
        const lr = getState().workflows.linkedResources || {};

        const form_has_usable_fields = (f) => Array.isArray(f?.fields) && f.fields.length > 0;
        const has_form = (id) => (lr.forms || []).some(
            (f) => (String(f.id) === String(id) || String(f._id) === String(id)) && form_has_usable_fields(f)
        );
        const has_email = (id) => (lr.emails || []).some((e) => String(e._id) === String(id));
        const has_article = (id) => (lr.articles || []).some((a) => String(a._id) === String(id));
        const has_salepoint = (id) => (lr.salepoints || []).some((s) => String(s._id) === String(id));

        const form_fetches = formIds.filter((id) => !has_form(id)).map((id) =>
            FormLayouts.get(id).then((j) => j.form).catch(() => null)
        );
        const email_fetches = emailLayoutIds.filter((id) => !has_email(id)).map((id) =>
            EmailLayouts.get(id).then((j) => j.layout).catch(() => null)
        );
        const article_fetches = articleIds.filter((id) => !has_article(id)).map((id) =>
            Articles.get(id).then((j) => j.article).catch(() => null)
        );
        const salepoint_fetches = salepointIds.filter((id) => !has_salepoint(id)).map((id) =>
            Salepoints.get(id).then((j) => j.salepoint).catch(() => null)
        );

        const [forms_r, emails_r, articles_r, salepoints_r] = await Promise.all([
            Promise.all(form_fetches),
            Promise.all(email_fetches),
            Promise.all(article_fetches),
            Promise.all(salepoint_fetches),
        ]);

        const patch = {};
        const forms_add = forms_r.filter(Boolean);
        const emails_add = emails_r.filter(Boolean);
        const articles_add = articles_r.filter(Boolean);
        const salepoints_add = salepoints_r.filter(Boolean);
        if (forms_add.length)
            patch.forms = forms_add;
        if (emails_add.length)
            patch.emails = emails_add;
        if (articles_add.length)
            patch.articles = articles_add;
        if (salepoints_add.length)
            patch.salepoints = salepoints_add;
        if (Object.keys(patch).length)
            dispatch(mergeWorkflowLinkedResources(patch));
    };
};
